import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAnalysisContext } from '../../context/AnalysisContext';
import { api } from '../../../../services/api';
import { Search, AlertCircle, CheckCircle2, Loader2, Microscope, FlaskConical } from 'lucide-react';

/**
 * Step 2: Cell Type Selection with Button Chips
 *
 * Features:
 * - Unified view with activable button chips
 * - Visual distinction between disease and normal cell types
 * - Search/filter functionality
 * - Clean white/light theme design
 * - Multi-select with visual feedback
 */

interface CellTypeData {
  cell_type: string;
  count: number;
  condition: 'disease' | 'normal';
}

export const Step2CellTypeSelector: React.FC = () => {
  const { selectedDisease, selectedCellTypes, toggleCellType, clearCellTypes, setCellTypes } = useAnalysisContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCondition, setFilterCondition] = useState<'all' | 'disease' | 'normal'>('all');

  // ========================================
  // FETCH CELL TYPES
  // ========================================

  const { data: cellTypesData, isLoading, error } = useQuery({
    queryKey: ['cellTypes', selectedDisease],
    queryFn: () => api.getCellTypes(selectedDisease!),
    enabled: !!selectedDisease,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // ========================================
  // PROCESS AND FILTER CELL TYPES
  // ========================================

  const { diseaseCellTypes, normalCellTypes, allCellTypes } = useMemo(() => {
    if (!cellTypesData || !Array.isArray(cellTypesData)) {
      return { diseaseCellTypes: [], normalCellTypes: [], allCellTypes: [] };
    }

    const disease = cellTypesData.filter((ct: CellTypeData) => ct.condition === 'disease');
    const normal = cellTypesData.filter((ct: CellTypeData) => ct.condition === 'normal');

    return {
      diseaseCellTypes: disease,
      normalCellTypes: normal,
      allCellTypes: cellTypesData,
    };
  }, [cellTypesData]);

  const filteredCellTypes = useMemo(() => {
    let types = allCellTypes;

    // Filter by condition
    if (filterCondition === 'disease') {
      types = diseaseCellTypes;
    } else if (filterCondition === 'normal') {
      types = normalCellTypes;
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      types = types.filter((ct: CellTypeData) =>
        ct.cell_type.toLowerCase().includes(query)
      );
    }

    return types;
  }, [allCellTypes, diseaseCellTypes, normalCellTypes, filterCondition, searchQuery]);

  // ========================================
  // HANDLERS
  // ========================================

  const handleToggle = (cellType: string, condition: 'disease' | 'normal') => {
    toggleCellType(cellType, condition);
  };

  const isSelected = (cellType: string, condition: 'disease' | 'normal'): boolean => {
    return selectedCellTypes[condition].includes(cellType);
  };

  const totalSelected = selectedCellTypes.disease.length + selectedCellTypes.normal.length;

  // Handler to select all cell types in current filter
  const handleSelectAll = () => {
    let typesToSelect: CellTypeData[] = [];

    if (filterCondition === 'disease') {
      typesToSelect = diseaseCellTypes;
    } else if (filterCondition === 'normal') {
      typesToSelect = normalCellTypes;
    } else {
      typesToSelect = allCellTypes;
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      typesToSelect = typesToSelect.filter((ct: CellTypeData) =>
        ct.cell_type.toLowerCase().includes(query)
      );
    }

    // Select all disease cell types from the filtered list
    const diseaseCellTypesToSelect = typesToSelect
      .filter((ct: CellTypeData) => ct.condition === 'disease')
      .map((ct: CellTypeData) => ct.cell_type);

    // Select all normal cell types from the filtered list
    const normalCellTypesToSelect = typesToSelect
      .filter((ct: CellTypeData) => ct.condition === 'normal')
      .map((ct: CellTypeData) => ct.cell_type);

    // Merge with existing selections (union) - use Array.from to avoid spread issue
    const newDisease = Array.from(new Set([...selectedCellTypes.disease, ...diseaseCellTypesToSelect]));
    const newNormal = Array.from(new Set([...selectedCellTypes.normal, ...normalCellTypesToSelect]));

    setCellTypes({ disease: newDisease, normal: newNormal });
  };

  // Check if all filtered types are selected
  const allFilteredSelected = useMemo(() => {
    if (filteredCellTypes.length === 0) return false;

    return filteredCellTypes.every((ct: CellTypeData) =>
      isSelected(ct.cell_type, ct.condition)
    );
  }, [filteredCellTypes, selectedCellTypes]);

  // ========================================
  // RENDER STATES
  // ========================================

  if (!selectedDisease) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-[var(--text-tertiary)] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
            No Disease Selected
          </h3>
          <p className="text-[var(--text-secondary)]">
            Please select a disease in Step 1 first.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="inline-block animate-spin h-12 w-12 text-[var(--origin-blue-500)] mb-4" />
          <p className="text-[var(--text-secondary)]">Loading cell types for {selectedDisease}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="origin-card-error max-w-md">
          <AlertCircle className="w-12 h-12 text-[var(--error)] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
            Failed to Load Cell Types
          </h3>
          <p className="text-[var(--text-secondary)] mb-4">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
        </div>
      </div>
    );
  }

  // ========================================
  // MAIN RENDER
  // ========================================

  return (
    <div className="flex-1 flex flex-col max-w-7xl mx-auto w-full px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-3xl font-bold text-[var(--text-primary)] mb-2">
              Select Cell Types
            </h2>
            <p className="text-[var(--text-secondary)] text-lg">
              Choose cell populations for comparative analysis of {selectedDisease}
            </p>
          </div>
          <div className="flex gap-2">
            {filteredCellTypes.length > 0 && !allFilteredSelected && (
              <button
                onClick={handleSelectAll}
                className="origin-btn origin-btn-secondary text-sm"
              >
                Select All {filterCondition !== 'all' && `(${filterCondition === 'disease' ? 'Disease' : 'Normal'})`}
              </button>
            )}
            {totalSelected > 0 && (
              <button
                onClick={() => clearCellTypes()}
                className="origin-btn origin-btn-ghost text-sm"
              >
                Clear All ({totalSelected})
              </button>
            )}
          </div>
        </div>

        {/* Selection Summary */}
        <div className="flex gap-4 text-sm">
          <div className="flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-[var(--error)]" />
            <span className="text-[var(--text-secondary)]">
              Disease: <strong className="text-[var(--text-primary)]">{selectedCellTypes.disease.length}</strong> selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Microscope className="w-4 h-4 text-[var(--success)]" />
            <span className="text-[var(--text-secondary)]">
              Normal: <strong className="text-[var(--text-primary)]">{selectedCellTypes.normal.length}</strong> selected
            </span>
          </div>
        </div>
      </div>

      {/* Controls Bar */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)]" />
          <input
            type="text"
            placeholder="Search cell types..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="origin-input pl-10 w-full"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
            >
              ✕
            </button>
          )}
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilterCondition('all')}
            className={`origin-btn ${
              filterCondition === 'all' ? 'origin-btn-primary' : 'origin-btn-secondary'
            }`}
          >
            All ({allCellTypes.length})
          </button>
          <button
            onClick={() => setFilterCondition('disease')}
            className={`origin-btn flex items-center gap-2 ${
              filterCondition === 'disease' ? 'origin-btn-primary' : 'origin-btn-secondary'
            }`}
          >
            <FlaskConical className="w-4 h-4" />
            Disease ({diseaseCellTypes.length})
          </button>
          <button
            onClick={() => setFilterCondition('normal')}
            className={`origin-btn flex items-center gap-2 ${
              filterCondition === 'normal' ? 'origin-btn-primary' : 'origin-btn-secondary'
            }`}
          >
            <Microscope className="w-4 h-4" />
            Normal ({normalCellTypes.length})
          </button>
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-4 text-sm text-[var(--text-secondary)]">
        {filteredCellTypes.length > 0 ? (
          <span>
            Showing {filteredCellTypes.length} cell type{filteredCellTypes.length !== 1 ? 's' : ''}
          </span>
        ) : searchQuery ? (
          <span>No cell types found matching "{searchQuery}"</span>
        ) : (
          <span>No cell types available</span>
        )}
      </div>

      {/* Cell Type Button Chips */}
      {filteredCellTypes.length > 0 ? (
        <div className="flex flex-wrap gap-3 pb-8">
          {filteredCellTypes.map((cellType: CellTypeData) => {
            const selected = isSelected(cellType.cell_type, cellType.condition);
            const isDiseaseType = cellType.condition === 'disease';

            return (
              <button
                key={`${cellType.condition}-${cellType.cell_type}`}
                onClick={() => handleToggle(cellType.cell_type, cellType.condition)}
                className={`origin-chip ${selected ? 'active' : ''} group relative`}
              >
                {/* Condition indicator dot */}
                <span
                  className={`inline-block w-2 h-2 rounded-full mr-2 ${
                    isDiseaseType ? 'bg-[var(--error)]' : 'bg-[var(--success)]'
                  }`}
                />

                {/* Cell type name */}
                <span className="font-medium">{cellType.cell_type}</span>

                {/* Selected checkmark */}
                {selected && (
                  <CheckCircle2 className="w-4 h-4 ml-2" />
                )}
              </button>
            );
          })}
        </div>
      ) : searchQuery ? (
        // No Search Results
        <div className="flex-1 flex items-center justify-center py-12">
          <div className="text-center max-w-md">
            <Search className="w-16 h-16 text-[var(--text-tertiary)] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
              No cell types found
            </h3>
            <p className="text-[var(--text-secondary)] mb-4">
              Try adjusting your search query or filter options.
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setFilterCondition('all');
              }}
              className="origin-btn origin-btn-secondary"
            >
              Clear Filters
            </button>
          </div>
        </div>
      ) : (
        // No Data
        <div className="flex-1 flex items-center justify-center py-12">
          <div className="text-center max-w-md">
            <AlertCircle className="w-16 h-16 text-[var(--text-tertiary)] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
              No cell types available
            </h3>
            <p className="text-[var(--text-secondary)]">
              No cell types found for the selected filter.
            </p>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="origin-card p-4 mt-6">
        <h4 className="font-semibold text-[var(--text-primary)] mb-3 text-sm">
          Legend
        </h4>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-[var(--error)]" />
            <span className="text-[var(--text-secondary)]">Disease cell types</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-[var(--success)]" />
            <span className="text-[var(--text-secondary)]">Normal cell types</span>
          </div>
        </div>
      </div>

      {/* Selection Status Bar */}
      {totalSelected > 0 && (
        <div className="fixed bottom-6 right-6 origin-card p-4 shadow-lg border-[var(--origin-blue-500)] border-2 max-w-md">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-[var(--origin-blue-500)] flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs text-[var(--text-secondary)] mb-1">Total Selected</p>
              <p className="font-semibold text-[var(--text-primary)]">
                {totalSelected} cell type{totalSelected !== 1 ? 's' : ''}
                {selectedCellTypes.disease.length > 0 && selectedCellTypes.normal.length > 0 && (
                  <span className="text-sm font-normal text-[var(--text-secondary)] ml-2">
                    ({selectedCellTypes.disease.length} disease, {selectedCellTypes.normal.length} normal)
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step2CellTypeSelector;
